import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Facebook, 
  Twitter, 
  Instagram, 
  Linkedin,
  Send,
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { useCreateContact } from "../hooks/useContactUsHooks";
import Footer from "../common/UI/Footer";
import styles from "./ContactUs.module.css";

const ContactUs = () => {
  const navigate = useNavigate();
  
  // Social media URLs
  const socialLinks = {
    facebook: "https://facebook.com/sunokahaniyan",
    twitter: "https://twitter.com/sunokahaniyan",
    instagram: "https://instagram.com/sunokahaniyan",
    linkedin: "https://linkedin.com/company/sunokahaniyan"
  };
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: ""
  });
  
  const [formErrors, setFormErrors] = useState({});
  const createContactMutation = useCreateContact();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ""
      });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }
    
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters long";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    createContactMutation.mutate(formData, {
      onSuccess: () => {
        // Reset form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "General Inquiry",
          message: ""
        });
        setFormErrors({});
      }
    });
  };

  const handleSocialClick = (platform) => {
    window.open(socialLinks[platform], "_blank");
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      details: "sunokahaniyan2025@gmail.com",
      subtitle: "We'll respond within 24 hours"
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+91 9810353504",
      subtitle: "Get in touch anytime"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details: "Building No 86, Tubewell Road",
      subtitle: "Near Hill Green Public School, Ambiwala, Uttarakhand, PIN: 248007"
    }
  ];



  return (
    <>
      <Helmet>
        <title>Contact Us - Suno Kahaniyan</title>
        <meta name="description" content="Get in touch with Suno Kahaniyan. We're here to help with support, feedback, partnerships, and any questions you might have." />
        <meta name="keywords" content="contact, support, help, feedback, Suno Kahaniyan" />
      </Helmet>

      <div className={styles.contactPage}>
        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Get in Touch</h1>
            <p className={styles.heroSubtitle}>
              We'd love to hear from you. Whether you have a question about our services, 
              need technical support, or want to share feedback, we're here to help.
            </p>
            <div className={styles.heroButtons}>
              <button 
                className={styles.primaryButton}
                onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
              >
                Send us a Message
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={() => navigate('/about')}
              >
                Learn More About Us
              </button>
            </div>
          </div>
        </section>

        {/* Contact Information Cards */}
        <section className={styles.contactInfoSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Contact Information</h2>
            <div className={styles.contactCards}>
              {contactInfo.map((info, index) => (
                <div key={index} className={styles.contactCard}>
                  <div className={styles.cardIcon}>
                    <info.icon size={24} />
                  </div>
                  <h3 className={styles.cardTitle}>{info.title}</h3>
                  <p className={styles.cardDetails}>{info.details}</p>
                  <p className={styles.cardSubtitle}>{info.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className={styles.contactFormSection}>
          <div className={styles.container}>
            <div className={styles.formWrapper}>
              <div className={styles.formContent}>
                <h2 className={styles.sectionTitle}>Send us a Message</h2>
                <p className={styles.formSubtitle}>
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
                
                <form onSubmit={handleSubmit} className={styles.contactForm}>
                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="name" className={styles.label}>
                        Full Name <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`${styles.input} ${formErrors.name ? styles.inputError : ''}`}
                        placeholder="Enter your full name"
                      />
                      {formErrors.name && (
                        <span className={styles.errorMessage}>
                          <AlertCircle size={16} />
                          {formErrors.name}
                        </span>
                      )}
                    </div>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="email" className={styles.label}>
                        Email Address <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`${styles.input} ${formErrors.email ? styles.inputError : ''}`}
                        placeholder="Enter your email address"
                      />
                      {formErrors.email && (
                        <span className={styles.errorMessage}>
                          <AlertCircle size={16} />
                          {formErrors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={styles.formRow}>
                    <div className={styles.formGroup}>
                      <label htmlFor="phone" className={styles.label}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={styles.input}
                        placeholder="Enter your phone number (optional)"
                      />
                    </div>
                    
                    <div className={styles.formGroup}>
                      <label htmlFor="subject" className={styles.label}>
                        Subject <span className={styles.required}>*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className={styles.select}
                      >
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Content Feedback">Content Feedback</option>
                        <option value="Partnership">Partnership</option>
                        <option value="Feature Request">Feature Request</option>
                        <option value="Bug Report">Bug Report</option>
                        <option value="Refund Request">Refund Request</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="message" className={styles.label}>
                      Message <span className={styles.required}>*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className={`${styles.textarea} ${formErrors.message ? styles.inputError : ''}`}
                      placeholder="Tell us how we can help you..."
                      rows={6}
                    />
                    {formErrors.message && (
                      <span className={styles.errorMessage}>
                        <AlertCircle size={16} />
                        {formErrors.message}
                      </span>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={createContactMutation.isLoading}
                    className={styles.submitButton}
                  >
                    {createContactMutation.isLoading ? (
                      <>
                        <div className={styles.spinner}></div>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>

                  {createContactMutation.isSuccess && (
                    <div className={styles.successMessage}>
                      <CheckCircle size={20} />
                      Your message has been sent successfully! We'll get back to you soon.
                    </div>
                  )}

                  {createContactMutation.isError && (
                    <div className={styles.errorAlert}>
                      <AlertCircle size={20} />
                      Failed to send message. Please try again or contact us directly.
                    </div>
                  )}
                </form>
              </div>

              {/* Social Media & Quick Info */}
              <div className={styles.sidebar}>
                <div className={styles.socialSection}>
                  <h3 className={styles.sidebarTitle}>Follow Us</h3>
                  <p className={styles.sidebarSubtitle}>
                    Stay updated with our latest stories and announcements
                  </p>
                  <div className={styles.socialButtons}>
                    <button
                      onClick={() => handleSocialClick("facebook")}
                      className={styles.socialButton}
                      aria-label="Follow us on Facebook"
                    >
                      <Facebook size={20} />
                      Facebook
                    </button>
                    <button
                      onClick={() => handleSocialClick("twitter")}
                      className={styles.socialButton}
                      aria-label="Follow us on Twitter"
                    >
                      <Twitter size={20} />
                      Twitter
                    </button>
                    <button
                      onClick={() => handleSocialClick("instagram")}
                      className={styles.socialButton}
                      aria-label="Follow us on Instagram"
                    >
                      <Instagram size={20} />
                      Instagram
                    </button>
                    <button
                      onClick={() => handleSocialClick("linkedin")}
                      className={styles.socialButton}
                      aria-label="Follow us on LinkedIn"
                    >
                      <Linkedin size={20} />
                      LinkedIn
                    </button>
                  </div>
                </div>

                <div className={styles.quickInfo}>
                  <h3 className={styles.sidebarTitle}>Quick Info</h3>
                  <div className={styles.infoItem}>
                    <strong>Response Time:</strong>
                    <span>Within 24 hours</span>
                  </div>
                  <div className={styles.infoItem}>
                    <strong>Support Hours:</strong>
                    <span>9 AM - 6 PM IST</span>
                  </div>
                  <div className={styles.infoItem}>
                    <strong>Languages:</strong>
                    <span>English, Hindi</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Information Section */}
        <section className={styles.companyInfoSection}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Company Information</h2>
            <div className={styles.companyInfoCard}>
              <div className={styles.companyHeader}>
                <h3 className={styles.companyTitle}>VOXMUSE PRODUCTIONS PRIVATE LIMITED</h3>
                <p className={styles.lastUpdated}>Last updated on 02-08-2025 17:06:42</p>
              </div>
              
              <div className={styles.companyDetails}>
                <div className={styles.infoRow}>
                  <strong>Legal Entity Name:</strong>
                  <span>VOXMUSE PRODUCTIONS PRIVATE LIMITED</span>
                </div>
                
                <div className={styles.infoRow}>
                  <strong>Registered Address:</strong>
                  <span>BUILDING NO 86, TUBEWELL ROAD, NEAR HILL GREEN PUBLIC SCHOOL, AMBIWALA, UTTARAKHAND, PIN: 248007</span>
                </div>
                
                <div className={styles.infoRow}>
                  <strong>Operational Address:</strong>
                  <span>BUILDING NO 86, TUBEWELL ROAD, NEAR HILL GREEN PUBLIC SCHOOL, AMBIWALA, UTTARAKHAND, PIN: 248007</span>
                </div>
                
                <div className={styles.infoRow}>
                  <strong>Telephone No:</strong>
                  <span>+91 9810353504</span>
                </div>
                
                <div className={styles.infoRow}>
                  <strong>E-Mail ID:</strong>
                  <span>sunokahaniyan2025@gmail.com</span>
                </div>
              </div>
            </div>
          </div>
        </section>


      </div>
      
      <Footer />
    </>
  );
};

export default ContactUs;
